import {
    Box,
    Skeleton,
    SortDirection,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { MouseEvent, ReactElement } from "react"

import InstallerApplicationRow from "./installerApplicationRow"
import InstallerApplicationListMobile from "./mobile/installerApplicationListMobile"
import {
    APPLICATION_ID_KW,
    ASC,
    DESC,
    ORDER_BY_TIMESTAMP,
    STATUS,
    LAST_UPDATED,
    CREATED_DATE,
} from "../../../constants/generalConstants"
import {
    TT_ADDRESS,
    TT_APPLICATION_ID,
    TT_POST_CODE,
    TT_SUBMITTED,
    TT_TECHNOLOGY,
    TT_RAISED_BY,
    TT_COMMISSIONING,
    TT_STATUS_INSTALLER,
    TT_LAST_UPDATED,
    TT_AMEND,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { useScreenSize } from "../../../hooks/useScreenSize"
import { IListApplications } from "../../../services/middleware/types/installerInterface/installerApplicationTypes"
import ZeroApplicationsMsg from "../../fallback/zeroApplicationsMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import LctAvatar from "../../pills/lct/lctAvatar"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const avatarSx = { width: "70px", pl: 1 }
export const AvatarHeadingCell = (): ReactElement => (
    <AppTableCell sx={avatarSx} />
)

interface IAvatarContentCell {
    lcts: string[]
}
export const AvatarContentCell = ({
    lcts,
}: IAvatarContentCell): ReactElement => (
    <AppTableCell sx={avatarSx}>
        <LctAvatar lcts={lcts} />
    </AppTableCell>
)

interface IHeading {
    title: string
    tooltip: string
    sortId: string
    center: boolean
    enableSort: boolean
    xlOnly: boolean
    adminOnly: boolean
    sortKey?: string
}

const HEADINGS: IHeading[] = [
    {
        title: "Address",
        tooltip: TT_ADDRESS,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Post Code",
        tooltip: TT_POST_CODE,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Raised By",
        tooltip: TT_RAISED_BY,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
        adminOnly: true,
    },
    {
        title: "Application ID",
        tooltip: TT_APPLICATION_ID,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: APPLICATION_ID_KW,
        xlOnly: true,
        adminOnly: false,
    },
    {
        title: "Submitted",
        tooltip: TT_SUBMITTED,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: CREATED_DATE,
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Last Updated",
        tooltip: TT_LAST_UPDATED,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: LAST_UPDATED,
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Status",
        tooltip: TT_STATUS_INSTALLER,
        center: true,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: STATUS,
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Technology",
        tooltip: TT_TECHNOLOGY,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: true,
        adminOnly: false,
    },
    {
        title: "Commissioning",
        tooltip: TT_COMMISSIONING,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
        adminOnly: false,
    },
    {
        title: "Amend",
        tooltip: TT_AMEND,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
        adminOnly: false,
    },
]

interface IApplicationTableProps {
    searchResults: IListApplications | undefined
    orderBy: string
    orderDirection: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    isLoading: boolean
    onSortChange: (sortKey: string) => void
}

/**
 * Displays listed applications. Enables ordering by clicking column headers
 */
const InstallerApplicationTable = ({
    searchResults,
    orderBy,
    orderDirection,
    setOrderBy,
    isLoading,
    onSortChange,
}: IApplicationTableProps): ReactElement => {
    const handleRequestSort = (
        event_: MouseEvent<unknown>,
        property: keyof IListApplications,
        sortKey: string
    ): void => {
        const isAsc = orderBy === property && orderDirection === ASC
        setOrderBy(String(sortKey || property), isAsc ? DESC : ASC)
        onSortChange(sortKey)
    }
    const createSortHandler =
        (property: keyof IListApplications, sortKey: string) =>
        (event: MouseEvent<unknown>) => {
            handleRequestSort(event, property, sortKey)
        }

    const { isInstallerAdmin } = useGetUserData()

    const filteredHeadings = isInstallerAdmin
        ? HEADINGS
        : HEADINGS.filter((heading) => heading.adminOnly === false)

    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(searchResults)) {
            return searchResults.length === 0
        }
        if (!searchResults) {
            return true
        }
        return false
    }

    const { isTabletOrMob } = useScreenSize()

    // Some headings should only be displayed on larger screens.
    // Note the component changes on md (tablet) breakpoint
    const { isXl } = useScreenSize()

    const headings = isXl
        ? filteredHeadings
        : filteredHeadings.filter((heading) => !heading.xlOnly)

    // eslint-disable-next-line no-nested-ternary
    return emptySearch() ? (
        <AppPaper>
            <ZeroApplicationsMsg />
        </AppPaper>
    ) : isTabletOrMob ? (
        <InstallerApplicationListMobile
            searchResults={searchResults as IListApplications}
            isLoading={isLoading}
        />
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <AvatarHeadingCell />
                        {headings.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.tooltip}
                                key={heading.title}
                            >
                                <AppTableCell
                                    sortDirection={
                                        orderBy === heading.sortId
                                            ? (orderDirection as SortDirection)
                                            : false
                                    }
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === heading.title}
                                        direction={
                                            orderBy === heading.sortId
                                                ? (orderDirection as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                                : (ASC as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                        }
                                        onClick={createSortHandler(
                                            heading.sortId as keyof IListApplications,
                                            heading.sortKey as string
                                        )}
                                        disabled={!heading.enableSort}
                                        sx={{
                                            "& .MuiTableSortLabel-icon": {
                                                opacity: 0,
                                            },
                                            "&:hover .MuiTableSortLabel-icon, &.Mui-active .MuiTableSortLabel-icon":
                                                {
                                                    opacity: 1,
                                                },
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                        {orderBy === heading.sortId ? (
                                            <Box
                                                component="span"
                                                sx={visuallyHidden}
                                            >
                                                {orderDirection === DESC
                                                    ? "sorted descending"
                                                    : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`applicationTable${v}`}
                              >
                                  <AppTableCell>
                                      <Skeleton
                                          variant="circular"
                                          width={40}
                                          height={40}
                                      />
                                  </AppTableCell>
                                  {Array.from(
                                      Array(headings.length).keys()
                                  ).map((num) => (
                                      <AppTableCell key={num}>
                                          <AppSkeleton />
                                      </AppTableCell>
                                  ))}
                              </AppTableContentRow>
                          ))
                        : (searchResults as IListApplications).map(
                              (application) => (
                                  <InstallerApplicationRow
                                      key={application.applicationId}
                                      application={application}
                                  />
                              )
                          )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default InstallerApplicationTable
